import { anonymizeEmailsInString } from "./anonymizeUtils.mjs";
import { hashForSentryUserId, sanitizeHttpHeadersSafe, scrubForExternalUseSafe } from "./piiUtils.mjs";

// src/utils/sentryScrub.ts
/** UUID v4 (and similar) — redact as opaque identifiers in Sentry payloads. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
/**
* Firebase-style UIDs and other opaque tokens often used in admin URLs and Pinia state.
* Conservative length band to limit false positives on long words.
*/
function isOpaqueIdString(s) {
	const t = s.trim();
	if (t.length === 0 || t.length > 128 || t.includes("@")) return false;
	if (UUID_RE.test(t)) return true;
	if (/^[a-zA-Z0-9]+$/.test(t) && t.length >= 20 && t.length <= 32) return true;
	return false;
}
function scrubMessageOpaqueIds(message) {
	return message.replace(/\b([a-zA-Z0-9]{20,32}|[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})\b/gi, (m) => isOpaqueIdString(m) ? "[REDACTED]" : m);
}
function scrubPathnameOpaqueIds(pathname) {
	if (!pathname || pathname === "/") return pathname;
	return pathname.split("/").map((seg) => {
		if (!seg) return seg;
		let decoded = seg;
		try {
			decoded = decodeURIComponent(seg);
		} catch {
			decoded = seg;
		}
		return isOpaqueIdString(decoded) ? "[id]" : seg;
	}).join("/");
}
function scrubSearchOpaqueIds(search) {
	if (!search || search === "?") return search;
	const out = (search.startsWith("?") ? search.slice(1) : search).split("&").map((pair) => {
		const eq = pair.indexOf("=");
		if (eq === -1) return isOpaqueIdString(pair) ? "[REDACTED]" : pair;
		const k = pair.slice(0, eq);
		const v = pair.slice(eq + 1);
		let dec = v;
		try {
			dec = decodeURIComponent(v);
		} catch {
			dec = v;
		}
		if (isOpaqueIdString(dec)) return `${k}=[REDACTED]`;
		return pair;
	});
	return (search.startsWith("?") ? "?" : "") + out.join("&");
}
/** Strips opaque id-like path segments and query values from a full URL or path+query. */
function scrubUrlOpaqueIds(urlStr) {
	if (!urlStr) return urlStr;
	try {
		const base = "https://sentry-scrub.invalid";
		const u = new URL(urlStr, base);
		if (u.origin === base || urlStr.startsWith("/")) return scrubPathnameOpaqueIds(u.pathname) + scrubSearchOpaqueIds(u.search);
		u.pathname = scrubPathnameOpaqueIds(u.pathname);
		u.search = scrubSearchOpaqueIds(u.search);
		return u.toString();
	} catch {
		const qIdx = urlStr.indexOf("?");
		if (qIdx === -1) return scrubPathnameOpaqueIds(urlStr);
		return scrubPathnameOpaqueIds(urlStr.slice(0, qIdx)) + scrubSearchOpaqueIds(urlStr.slice(qIdx));
	}
}
function sanitizeOpaqueIdsDeepInner(value, cache, active) {
	if (value === null || value === void 0) return value;
	if (typeof value === "string") return isOpaqueIdString(value) ? "[REDACTED]" : value;
	if (typeof value !== "object") return value;
	if (value instanceof Date) return value;
	if (cache.has(value)) return cache.get(value);
	if (active.has(value)) return "[CIRCULAR]";
	active.add(value);
	if (Array.isArray(value)) {
		const out = [];
		cache.set(value, out);
		for (const item of value) out.push(sanitizeOpaqueIdsDeepInner(item, cache, active));
		active.delete(value);
		return out;
	}
	if (value instanceof Map) {
		const out = /* @__PURE__ */ new Map();
		cache.set(value, out);
		for (const [k, v] of value) {
			const mapKey = typeof k === "string" ? isOpaqueIdString(k) ? hashForSentryUserId(k) : k : sanitizeOpaqueIdsDeepInner(k, cache, active);
			out.set(mapKey, sanitizeOpaqueIdsDeepInner(v, cache, active));
		}
		active.delete(value);
		return out;
	}
	if (value instanceof Set) {
		const out = /* @__PURE__ */ new Set();
		cache.set(value, out);
		for (const el of value) out.add(sanitizeOpaqueIdsDeepInner(el, cache, active));
		active.delete(value);
		return out;
	}
	const obj = value;
	const out = {};
	cache.set(value, out);
	for (const key of Object.keys(obj)) {
		const v = obj[key];
		const safeKey = isOpaqueIdString(key) ? hashForSentryUserId(key) : key;
		out[safeKey] = sanitizeOpaqueIdsDeepInner(v, cache, active);
	}
	active.delete(value);
	return out;
}
/**
* Recursively redacts Firebase/UUID-like strings. Object and Map keys that are opaque
* id strings are replaced with {@link hashForSentryUserId} so multiple ids stay distinct.
*/
function sanitizeOpaqueIdsDeep(value) {
	return sanitizeOpaqueIdsDeepInner(value, /* @__PURE__ */ new WeakMap(), /* @__PURE__ */ new WeakSet());
}
/**
* Pinia + Sentry: run generic outbound scrub, then strip opaque ids so admin customer UIDs
* do not reach Sentry in store snapshots.
*/
function scrubPiniaStateForSentry(state) {
	return sanitizeOpaqueIdsDeep(scrubForExternalUseSafe(state));
}
function scrubSentryEventInPlace(event) {
	if (event.extra && typeof event.extra === "object") event.extra = sanitizeOpaqueIdsDeep(scrubForExternalUseSafe(event.extra));
	if (event.tags && typeof event.tags === "object") event.tags = sanitizeOpaqueIdsDeep(scrubForExternalUseSafe(event.tags));
	if (event.contexts && typeof event.contexts === "object") event.contexts = sanitizeOpaqueIdsDeep(scrubForExternalUseSafe(event.contexts));
	if (event.exception !== void 0) event.exception = sanitizeOpaqueIdsDeep(scrubForExternalUseSafe(event.exception));
	const rawBreadcrumbs = event.breadcrumbs;
	if (Array.isArray(rawBreadcrumbs) && rawBreadcrumbs.length > 0) event.breadcrumbs = rawBreadcrumbs.map((b) => {
		if (typeof b !== "object" || b === null) return b;
		const crumb = b;
		let message = crumb.message;
		if (typeof message === "string" && message) try {
			message = scrubMessageOpaqueIds(anonymizeEmailsInString(message));
		} catch {
			message = "[REDACTED]";
		}
		let data = crumb.data;
		if (data !== void 0) data = sanitizeOpaqueIdsDeep(scrubForExternalUseSafe(data));
		return {
			...crumb,
			message,
			data
		};
	});
	const req = event.request;
	if (req && typeof req === "object" && !Array.isArray(req)) {
		const request = req;
		if (typeof request.url === "string" && request.url) try {
			request.url = scrubUrlOpaqueIds(anonymizeEmailsInString(request.url));
		} catch {
			request.url = "[REDACTED]";
		}
		if (typeof request.query_string === "string" && request.query_string) try {
			request.query_string = scrubSearchOpaqueIds(anonymizeEmailsInString(request.query_string));
		} catch {
			request.query_string = "[REDACTED]";
		}
		if (request.data !== void 0) request.data = sanitizeOpaqueIdsDeep(scrubForExternalUseSafe(request.data));
		if (request.headers !== void 0) {
			const sanitized = sanitizeHttpHeadersSafe(request.headers);
			if (sanitized) request.headers = sanitized;
		}
		if (request.cookies !== void 0) request.cookies = sanitizeOpaqueIdsDeep(scrubForExternalUseSafe(request.cookies));
	}
	if (typeof event.transaction === "string" && event.transaction) try {
		event.transaction = scrubUrlOpaqueIds(anonymizeEmailsInString(event.transaction));
	} catch {
		event.transaction = "[REDACTED]";
	}
}
/**
* Reduces PII/PHI in a Sentry error or transaction event before ingest.
* Safe to use from `beforeSend` / `beforeSendTransaction`; mutates the event in place.
*/
function scrubSentryEvent(event) {
	if (typeof event !== "object" || event === null) return event;
	scrubSentryEventInPlace(event);
	return event;
}

export { isOpaqueIdString, sanitizeOpaqueIdsDeep, scrubPiniaStateForSentry, scrubSentryEvent, scrubUrlOpaqueIds };
//# sourceMappingURL=sentryScrub.mjs.map